import {
  PROPOSAL_STATUSES,
  daysInStatus,
  daysUntil,
  isStale,
  type ProposalStatus,
  type StaleDays,
} from '@sales-rules';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  brl,
  dmy,
  formatCnpj,
  formatCpf,
  operatorClass,
  relationshipTag,
  relativeEffective,
  salesApi,
  statusLabel,
  typeLabel,
  type SalesProposal,
} from '../../lib/sales';

const SITUATION_LABEL = { L: 'Limpo', R: 'Repique', IN: 'Inadimplente' } as const;

export interface ProposalCardActions {
  onCopy: (value: string, label: string) => void;
  onStatus: (proposal: SalesProposal, status: ProposalStatus) => void;
  onEdit: (proposal: SalesProposal) => void;
  onEconomyEnergy: (proposal: SalesProposal) => void;
  onOpenPdf: (proposal: SalesProposal) => void;
  /** Linhas extras dentro do card (entrevistas, V3). */
  extra?: (proposal: SalesProposal) => ReactNode;
  /** Botões extras na barra de ações (+ Entrevista, V3). */
  extraActions?: (proposal: SalesProposal) => ReactNode;
}

/** O card da proposta, igual ao do artefato. */
export function ProposalCard({
  proposal,
  today,
  staleDays,
  showSeller,
  actions,
}: {
  proposal: SalesProposal;
  today: string;
  staleDays: StaleDays;
  showSeller: boolean;
  actions: ProposalCardActions;
}) {
  const open = proposal.status !== 'ENCERRADO';
  const days = daysUntil(proposal.effectiveDate, today);
  const urgency = open && days !== null ? (days < 0 ? 'crit' : days <= 7 ? 'warn' : '') : '';
  const dayClass = open && days !== null ? (days < 0 ? 'late' : days <= 7 ? 'soon' : '') : '';
  const inStatus = daysInStatus(proposal.statusSince);
  const stale = isStale(proposal, staleDays);
  const total = proposal.beneficiaries.reduce((sum, item) => sum + item.monthlyCents, 0);
  const clean = proposal.beneficiaries
    .filter((item) => item.situation === 'L')
    .reduce((sum, item) => sum + item.monthlyCents, 0);
  const hasRebound = proposal.beneficiaries.some(
    (item) => item.situation && item.situation !== 'L',
  );
  const count = proposal.beneficiaries.length;

  return (
    <article className={`card ${urgency}`} data-s={proposal.status}>
      <div className="top">
        <div className="row1">
          {proposal.requestNumber && (
            <button
              className="soli"
              type="button"
              title="Copiar nº da proposta"
              onClick={() => actions.onCopy(proposal.requestNumber as string, 'Nº da proposta')}
            >
              {proposal.requestNumber}
            </button>
          )}
          <span className={`chip op ${operatorClass(proposal.operator)}`}>
            {proposal.operator}
          </span>
          <span className="chip">{typeLabel(proposal.type)}</span>
          {proposal.plan && <span className="chip">{proposal.plan}</span>}
        </div>
        <div className="emp">
          {proposal.company}
          {proposal.cnpj && (
            <span className="cnpj">
              {' '}
              · CNPJ{' '}
              <button
                className="cpf"
                type="button"
                title="Copiar CNPJ"
                onClick={() => actions.onCopy(formatCnpj(proposal.cnpj), 'CNPJ')}
              >
                {formatCnpj(proposal.cnpj)}
              </button>
            </span>
          )}
        </div>
        <div className="meta">
          <span>
            {dmy(proposal.effectiveDate)} ·{' '}
            <span className={dayClass}>{relativeEffective(days)}</span>
          </span>
          {open && inStatus !== null && (
            <span className={stale ? 'late' : ''}>
              {inStatus === 0
                ? 'mudou de status hoje'
                : `em ${statusLabel(proposal.status)} há ${inStatus} ${inStatus === 1 ? 'dia' : 'dias'}`}
            </span>
          )}
          {showSeller && (
            <span>
              Vendedor: <b>{proposal.seller?.name ?? proposal.sellerName ?? 'sem vendedor'}</b>
            </span>
          )}
          {proposal.lead && (
            <span>
              Negociado por{' '}
              <Link
                to={`/leads/${proposal.lead.id}`}
                className="linkbtn"
                title="Abrir o lead no CRM"
              >
                {proposal.lead.name}
              </Link>
            </span>
          )}
        </div>
        {actions.extra?.(proposal)}
        {proposal.status === 'PENDENTE' && (
          <div className="pendline">
            <b>Pendência:</b> {proposal.pendingText || <i>não informada</i>}
          </div>
        )}
        {count > 0 && (
          <ul className="names">
            {proposal.beneficiaries.map((item, index) => (
              <li key={index} className={`lr-${item.situation ?? 'X'}`}>
                <i />
                <span className="nm">
                  {item.name || '—'}{' '}
                  {relationshipTag(item.relationship) && (
                    <span className={`vt ${item.relationship === 'TITULAR' ? 'tit' : 'dep'}`}>
                      {relationshipTag(item.relationship)}
                    </span>
                  )}
                  {item.situation && item.situation !== 'L' && (
                    <em> {SITUATION_LABEL[item.situation]}</em>
                  )}
                  {!item.situation && <em> sem situação</em>}
                </span>
                <span className="nv num">
                  {item.monthlyCents ? brl(item.monthlyCents) : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className={`meta num ctot${count ? ' sep' : ''}`}>
          <span>
            <b>{proposal.lives}</b> {proposal.lives === 1 ? 'vida' : 'vidas'}
          </span>
          <span>
            <b>{brl(total)}</b>
          </span>
          {hasRebound && clean > 0 && (
            <span className="limpo">
              limpo <b>{brl(clean)}</b>
            </span>
          )}
        </div>
        {proposal.operator === 'Unimed Fortaleza' && (
          <button
            type="button"
            className={`eco${proposal.economyEnergyAt ? ' on' : ''}`}
            title={
              proposal.economyEnergyAt
                ? 'Clique para desmarcar'
                : 'Clique quando indicar o cliente no sistema da Economy Energy'
            }
            onClick={() => actions.onEconomyEnergy(proposal)}
          >
            <span className="eco-ic" aria-hidden="true">
              ⚡
            </span>
            {proposal.economyEnergyAt ? (
              `Indicado na Economy Energy em ${dmy(proposal.economyEnergyAt)}`
            ) : (
              <span>
                Economy Energy: <b>não indicado</b>
              </span>
            )}
          </button>
        )}
        {proposal.notes && <div className="cobs">{proposal.notes}</div>}
      </div>
      <div className="acts">
        <select
          className="stsel"
          aria-label="Mudar status"
          value={proposal.status}
          onChange={(event) => actions.onStatus(proposal, event.target.value as ProposalStatus)}
        >
          {PROPOSAL_STATUSES.map((status) => (
            <option key={status} value={status}>
              {statusLabel(status)}
            </option>
          ))}
        </select>
        <button type="button" className="act" onClick={() => actions.onEdit(proposal)}>
          Editar
        </button>
        {actions.extraActions?.(proposal)}
        {proposal.pdf && (
          <button
            type="button"
            className="act"
            title={proposal.pdf.fileName}
            onClick={() => actions.onOpenPdf(proposal)}
          >
            Ver PDF
          </button>
        )}
      </div>
      {count > 0 && (
        <details className="bl">
          <summary>
            {count} {count === 1 ? 'beneficiário' : 'beneficiários'}
          </summary>
          <div className="benef">
            {proposal.beneficiaries.map((item, index) => (
              <div className="b" key={index}>
                <span className="n">{item.name}</span>
                <span
                  className={`v num ${item.situation === 'L' ? 'pos' : item.situation ? 'neg' : ''}`}
                >
                  {brl(item.monthlyCents)}
                </span>
                <span className="c">
                  {item.cpf ? (
                    <button
                      className="cpf"
                      type="button"
                      title="Copiar CPF"
                      onClick={() => actions.onCopy(formatCpf(item.cpf), 'CPF')}
                    >
                      {formatCpf(item.cpf)}
                    </button>
                  ) : (
                    <span>sem CPF</span>
                  )}
                  {item.situation && item.situation !== 'L' && (
                    <span style={{ color: 'var(--crit)', fontWeight: 600 }}>
                      {SITUATION_LABEL[item.situation]}
                    </span>
                  )}
                  {relationshipTag(item.relationship) && (
                    <span>{relationshipTag(item.relationship)}</span>
                  )}
                  {item.notes && item.notes !== item.relationship && <span>{item.notes}</span>}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
      <StatusHistory proposalId={proposal.id} />
    </article>
  );
}

/** Histórico de status com quem mudou (a auditoria): carrega só quando abre. */
function StatusHistory({ proposalId }: { proposalId: string }) {
  const [open, setOpen] = useState(false);
  const history = useQuery({
    queryKey: ['sales', 'history', proposalId],
    queryFn: async () => (await salesApi.get(proposalId)).history ?? [],
    enabled: open,
  });
  return (
    <details
      className="bl"
      onToggle={(event) => setOpen((event.target as HTMLDetailsElement).open)}
    >
      <summary>Histórico de status</summary>
      <div className="benef">
        {history.isLoading && <div className="empty">Carregando…</div>}
        {history.data?.map((entry, index) => (
          <div className="b" key={index}>
            <span className="n">
              {entry.fromStatus ? `${statusLabel(entry.fromStatus)} → ` : ''}
              {statusLabel(entry.toStatus)}
            </span>
            <span className="v num">
              {new Date(entry.changedAt).toLocaleString('pt-BR', {
                dateStyle: 'short',
                timeStyle: 'short',
              })}
            </span>
            <span className="c">{entry.changedBy.name ?? '—'}</span>
          </div>
        ))}
      </div>
    </details>
  );
}
